import { useMutation } from '@tanstack/react-query'

import {
  CreateFieldInput,
  PropertyFieldInput,
  UpdateFieldInput,
} from './property.type'
import { buildFieldMutationValueInput } from './field.value'
import { getAmplifyClient } from '@/services/amplifyClient'
import * as queries from '@/services/graphql'
import { PropertyDefinition } from './property.server'

export type CreatePropertyFieldInput = PropertyFieldInput & {
  propertyID: string
  propertyFieldsId: string
  organizationFieldsId: string
}

export type UpsertPropertyField =
  | {
      type: 'create'
      input: CreatePropertyFieldInput
    }
  | {
      type: 'update'
      input: UpdateFieldInput
    }
  | {
      type: 'duplicate'
      input: {
        id: string
        propertyID: string
        fieldID: string
      }
    }
  | {
      type: 'delete'
      input: {
        id: string
      }
    }

export const useUpsertPropertyField = () => {
  return useMutation({
    mutationKey: ['upsertField'],
    mutationFn: upsertField,
    onMutate: (variables, context) => {
      const { type, input } = variables
      context.client.cancelQueries({ queryKey: ['properties'] })

      // add or remove from cache
      context.client.setQueriesData(
        { queryKey: ['properties'] },
        (oldData: PropertyDefinition) => {
          if (!oldData) return oldData
          if (type === 'create') {
            return {
              ...oldData,
              fields: {
                items: [
                  ...oldData.fields.items,
                  { id: input.propertyFieldsId, field: input },
                ],
              },
            }
          }
          if (type === 'update') {
            return {
              ...oldData,
              fields: {
                items: oldData.fields.items.map((item) =>
                  item.field?.id === input.id
                    ? { ...item, field: { ...item.field, ...input } }
                    : item,
                ),
              },
            }
          }
          if (type === 'delete') {
            return {
              ...oldData,
              fields: {
                items: oldData.fields.items.filter(
                  (item) => item.id !== input.id,
                ),
              },
            }
          }
          return oldData
        },
      )
    },
    onSuccess: (_, _variables, _result, context) => {
      context.client.invalidateQueries({
        queryKey: ['properties'],
      })
    },
  })
}

const upsertField = async (params: UpsertPropertyField) => {
  const { type, input } = params
  switch (type) {
    case 'create':
      return createPropertyField(input)
    case 'update':
      return updatePropertyField(input)
    case 'duplicate':
      return duplicatePropertyField(input)
    case 'delete':
      return deletePropertyField(input)
    default:
      throw new Error('Invalid upsert type')
  }
}

const createPropertyField = async (
  params: PropertyFieldInput & {
    propertyID: string
    propertyFieldsId: string
    organizationFieldsId: string
  },
) => {
  const { propertyID, propertyFieldsId: id, ...input } = params

  const createFieldInput = {
    ...input,
    ...buildFieldMutationValueInput({
      value: input.value,
      type: input.type,
    }),
  }

  try {
    const client = getAmplifyClient()
    await client.graphql({
      query: queries.createPropertyFieldMutation,
      variables: {
        createFieldInput,
        createPropertyFieldsInput: {
          id,
          propertyID,
          fieldID: input.id,
        },
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

const duplicatePropertyField = async (input: {
  id: string
  propertyID: string
  fieldID: string
}) => {
  try {
    const client = getAmplifyClient()
    await client.graphql({
      query: queries.createPropertyFieldsMutation,
      variables: {
        input,
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

const deletePropertyField = async ({ id }: { id: string }) => {
  try {
    const client = getAmplifyClient()
    await client.graphql({
      query: queries.deletePropertyFieldsMutation,
      variables: {
        input: {
          id,
        },
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

const updatePropertyField = async (params: UpdateFieldInput) => {
  const { id, label, value, type, metadata, status } = params
  try {
    const client = getAmplifyClient()
    await client.graphql({
      query: queries.updateFieldMutation,
      variables: {
        input: {
          id,
          ...(label && { label }),
          ...buildFieldMutationValueInput({ value, type }),
          ...(metadata && { metadata }),
          ...(status && { status }),
        },
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const useCreateField = () => {
  return useMutation({
    mutationKey: ['createField'],
    mutationFn: createField,
    onSuccess: (_, variables, _result, context) => {
      if (variables.type === 'LIST') {
        context.client.invalidateQueries({
          queryKey: ['lists-all'],
        })
      }
      context.client.invalidateQueries({
        queryKey: ['properties'],
      })
    },
  })
}

const createField = async (params: CreateFieldInput) => {
  try {
    const client = getAmplifyClient()
    await client.graphql({
      query: queries.createFieldMutation,
      variables: { input: params },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}
